import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import * as Highcharts from 'highcharts';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { Httpservice } from '../../../services/httpservice';
import { DashboardHeaderComponent } from '../../structure/header/header';
import { DashboardSidebarComponent } from '../../structure/sidebar/sidebar';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { AppPreferencesService } from '../../../services/app-preferences';
import {
  OutageAnalyticsChartPoint,
  OutageAnalyticsDateMode,
  OutageAnalyticsComboPoint,
  OutageAnalyticsEntityOption,
  OutageAnalyticsPagination,
  OutageAnalyticsRow,
  OutageAnalyticsTimelinePoint,
  OutageAnalyticsSummary,
} from './outage-analytics.interfaces';

@Component({
  selector: 'app-outage-analytics-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DashboardSidebarComponent, DashboardHeaderComponent],
  templateUrl: './outage-analytics.html',
  styleUrl: './outage-analytics.scss',
})
export class OutageAnalyticsPage implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('outageTypeChartContainer') outageTypeChartContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('categoryChartContainer') categoryChartContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('technologyChartContainer') technologyChartContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('mostTrippedChartContainer') mostTrippedChartContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('upcomingChartContainer') upcomingChartContainer?: ElementRef<HTMLDivElement>;

  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly pageSizeOptions = [15, 25, 50, 100];
  protected readonly pageSizeControl = new FormControl(10, { nonNullable: true });
  protected readonly todayEntityControl = new FormControl<number | null>(null);
  protected readonly percentwiseEntityControl = new FormControl<number | null>(null);
  protected readonly trippedEntityControl = new FormControl<number | null>(null);
  protected readonly trippedFromDateControl = new FormControl(this.formatQueryDate(this.getDefaultChartStartDate()), { nonNullable: true });
  protected readonly trippedToDateControl = new FormControl(this.formatQueryDate(new Date()), { nonNullable: true });
  protected readonly upcomingEntityControl = new FormControl<number | null>(null);
  protected readonly chartFromDateControl = new FormControl(this.formatQueryDate(this.getDefaultChartStartDate()), { nonNullable: true });
  protected readonly chartToDateControl = new FormControl(this.formatQueryDate(new Date()), { nonNullable: true });
  protected readonly chartTypeControl = new FormControl('capacity', { nonNullable: true });
  protected readonly dateModes: { key: OutageAnalyticsDateMode; label: string }[] = [
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'today', label: 'Today' },
  ];

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected mostTrippedLoading = false;
  protected upcomingLoading = false;
  protected chartsLoading = false;
  protected selectedDateMode: OutageAnalyticsDateMode = 'today';
  protected selectedDateLabel = '';
  protected pagination: OutageAnalyticsPagination = {
    page_no: 1,
    page_size: 15,
    total_records: 0,
    total_pages: 0,
  };

  protected outageRows: OutageAnalyticsRow[] = [];
  protected outageSummary: OutageAnalyticsSummary = {
    selected_date: '',
    total_records: 0,
    total_capacity: 0,
    dominant_outage_type: '-',
    dominant_outage_count: 0,
  };
  protected outageTypeWise: OutageAnalyticsChartPoint[] = [];
  protected categoryWise: OutageAnalyticsChartPoint[] = [];
  protected technologyWise: OutageAnalyticsChartPoint[] = [];
  protected entities: OutageAnalyticsEntityOption[] = [];
  protected mostTrippedPoints: OutageAnalyticsComboPoint[] = [];
  protected upcomingPoints: OutageAnalyticsTimelinePoint[] = [];

  private readonly destroyRef = inject(DestroyRef);
  private outageTypeChart?: Highcharts.Chart;
  private categoryChart?: Highcharts.Chart;
  private technologyChart?: Highcharts.Chart;
  private mostTrippedChart?: Highcharts.Chart;
  private upcomingChart?: Highcharts.Chart;
  private viewReady = false;

  constructor(
    private readonly httpSvc: Httpservice,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly cdr: ChangeDetectorRef,
    private readonly preferencesSvc: AppPreferencesService,
  ) {}

  ngOnInit(): void {
    this.pageSizeControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadTodayOutages(1);
      });

    this.todayEntityControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadTodayOutages(1));

    this.chartFromDateControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadCharts());

    this.chartToDateControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadCharts());

    this.chartTypeControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadCharts());

    this.percentwiseEntityControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadCharts());

    this.trippedEntityControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadMostTripped());

    this.trippedFromDateControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadMostTripped());

    this.trippedToDateControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadMostTripped());

    this.upcomingEntityControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadUpcomingOutages());

    this.loadEntities();
    this.loadTodayOutages();
    this.loadMostTripped();
    this.loadUpcomingOutages();
    this.loadCharts();
    this.selectedDateLabel = this.formatSelectedDate(this.selectedDateMode);
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.renderCharts();
  }

  ngOnDestroy(): void {
    this.outageTypeChart?.destroy();
    this.categoryChart?.destroy();
    this.technologyChart?.destroy();
    this.mostTrippedChart?.destroy();
    this.upcomingChart?.destroy();
  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }

    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected selectDateMode(mode: OutageAnalyticsDateMode): void {
    if (this.selectedDateMode === mode) {
      return;
    }

    this.selectedDateMode = mode;
    this.selectedDateLabel = this.formatSelectedDate(mode);
    this.loadTodayOutages(1);
  }

  protected trackByRow(_: number, row: OutageAnalyticsRow): number {
    return row.id;
  }

  protected trackByEntity(_: number, entity: OutageAnalyticsEntityOption): number {
    return entity.id;
  }

  protected getTotalCapacityLabel(): string {
    return `${this.outageSummary.total_capacity.toLocaleString(undefined, { maximumFractionDigits: 2 })} MW`;
  }

  protected getDateLabel(): string {
    return this.selectedDateLabel;
  }

  protected getTodayOutageTitle(): string {
    return `${this.getDateLabel()} Outage`;
  }

  protected getMostTrippedTitle(): string {
    return 'Most Tripped Units in Particular Duration';
  }

  protected getUpcomingTitle(): string {
    return 'Upcoming Outage';
  }

  protected getChartTypeLabel(): string {
    const value = this.chartTypeControl.value;
    return value === 'count' ? 'Count' : 'Capacity';
  }

  protected getSummaryCards(): { label: string; value: string; note: string }[] {
    return [
      {
        label: 'Outage Records',
        value: String(this.outageSummary.total_records),
        note: 'Rows on the selected date',
      },
      {
        label: 'Total Capacity',
        value: this.getTotalCapacityLabel(),
        note: 'Summed from substation capacity',
      },
      {
        label: 'Dominant Type',
        value: this.outageSummary.dominant_outage_type || '-',
        note: `${this.outageSummary.dominant_outage_count} records`,
      },
    ];
  }

  protected formatCapacity(value: number): string {
    if (!Number.isFinite(value)) {
      return '-';
    }

    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  protected formatShare(value: number): string {
    if (!Number.isFinite(value)) {
      return '-';
    }

    return `${value.toFixed(2)}%`;
  }

  protected canShowPagination(): boolean {
    return this.pagination.total_records > 0;
  }

  protected getPaginationLabel(): string {
    if (!this.pagination.total_records) {
      return 'No records';
    }

    const start = (this.pagination.page_no - 1) * this.pagination.page_size + 1;
    const end = Math.min(this.pagination.page_no * this.pagination.page_size, this.pagination.total_records);
    return `Showing ${start} to ${end} of ${this.pagination.total_records} results`;
  }

  protected getPaginationItems(): number[] {
    const totalPages = this.pagination.total_pages;
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const currentPage = this.pagination.page_no;
    const pages = new Set<number>([1, totalPages]);

    for (let page = currentPage - 1; page <= currentPage + 1; page += 1) {
      if (page > 1 && page < totalPages) {
        pages.add(page);
      }
    }

    return Array.from(pages)
      .sort((left, right) => left - right)
      .reduce<number[]>((accumulator, page) => {
        const previous = accumulator[accumulator.length - 1];
        if (previous !== undefined && page - previous > 1) {
          accumulator.push(-1);
        }

        accumulator.push(page);
        return accumulator;
      }, []);
  }

  protected goToPreviousPage(): void {
    this.goToPage(this.pagination.page_no - 1);
  }

  protected goToNextPage(): void {
    this.goToPage(this.pagination.page_no + 1);
  }

  protected goToPage(pageNo: number): void {
    if (pageNo < 1 || pageNo > this.pagination.total_pages) {
      return;
    }

    this.loadTodayOutages(pageNo);
  }

  protected formatSelectedDate(mode: OutageAnalyticsDateMode): string {
    const targetDate = this.resolveDateForMode(mode);
    return targetDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  private loadTodayOutages(pageNo = this.pagination.page_no): void {
    const queryDate = this.formatQueryDate(this.resolveDateForMode(this.selectedDateMode));
    const queryRoute = this.buildQueryRoute(AuthEndPoints.GET_TODAY_OUTAGE_ANALYTICS, {
      outage_date: queryDate,
      page_no: pageNo,
      page_size: this.pageSizeControl.value,
      entity_id: this.todayEntityControl.value,
    });

    this.isLoading = true;

    this.httpSvc
      .requestCall(queryRoute, ApiMethod.GET, '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (today) => {
          this.isLoading = false;
          this.outageRows = this.extractTodayRows(today);
          const summary = this.extractSummary(today) ?? this.outageSummary;
          this.outageSummary = summary;
          this.pagination = this.extractPagination(today) ?? {
            page_no: pageNo,
            page_size: this.pageSizeControl.value,
            total_records: summary.total_records,
            total_pages: summary.total_records
              ? Math.max(Math.ceil(summary.total_records / this.pageSizeControl.value), 1)
              : 0,
          };
          this.cdr.markForCheck();
        },
        error: () => {
          this.outageRows = [];
          this.outageSummary = {
            selected_date: queryDate,
            total_records: 0,
            total_capacity: 0,
            dominant_outage_type: '-',
            dominant_outage_count: 0,
          };
          this.pagination = {
            page_no: pageNo,
            page_size: this.pageSizeControl.value,
            total_records: 0,
            total_pages: 0,
          };
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadCharts(): void {
    const fromDate = this.chartFromDateControl.value;
    const toDate = this.chartToDateControl.value;
    const chartType = this.chartTypeControl.value;
    const queryRoute = this.buildQueryRoute(AuthEndPoints.GET_OUTAGE_PERCENTWISE_ANALYTICS, {
      from_date: fromDate,
      to_date: toDate,
      chart_type: chartType,
      entity_id: this.percentwiseEntityControl.value,
    });

    this.chartsLoading = true;

    this.httpSvc
      .requestCall(queryRoute, ApiMethod.GET, '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (percentwise) => {
          this.chartsLoading = false;
          const charts = this.extractCharts(percentwise);
          this.outageTypeWise = charts.outage_type_wise;
          this.categoryWise = charts.category_wise;
          this.technologyWise = charts.technology_wise;
          this.renderCharts();
          this.cdr.markForCheck();
        },
        error: () => {
          this.chartsLoading = false;
          this.outageTypeWise = [];
          this.categoryWise = [];
          this.technologyWise = [];
          this.renderCharts();
          this.cdr.markForCheck();
        },
      });
  }

  private loadMostTripped(): void {
    const queryRoute = this.buildQueryRoute(AuthEndPoints.GET_MOST_TRIPPED_OUTAGE_ANALYTICS, {
      from_date: this.trippedFromDateControl.value,
      to_date: this.trippedToDateControl.value,
      entity_id: this.trippedEntityControl.value,
    });

    this.mostTrippedLoading = true;

    this.httpSvc
      .requestCall(queryRoute, ApiMethod.GET, '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.mostTrippedLoading = false;
          const value = this.normalizeResponseValue(response);
          this.mostTrippedPoints = this.extractComboPoints(value);
          this.renderCharts();
          this.cdr.markForCheck();
        },
        error: () => {
          this.mostTrippedLoading = false;
          this.mostTrippedPoints = [];
          this.renderCharts();
          this.cdr.markForCheck();
        },
      });
  }

  private loadUpcomingOutages(): void {
    const queryRoute = this.buildQueryRoute(AuthEndPoints.GET_UPCOMING_OUTAGE_ANALYTICS, {
      entity_id: this.upcomingEntityControl.value,
      days: 10,
    });

    this.upcomingLoading = true;

    this.httpSvc
      .requestCall(queryRoute, ApiMethod.GET, '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.upcomingLoading = false;
          const value = this.normalizeResponseValue(response);
          this.upcomingPoints = this.extractTimelinePoints(value);
          this.renderCharts();
          this.cdr.markForCheck();
        },
        error: () => {
          this.upcomingLoading = false;
          this.upcomingPoints = [];
          this.renderCharts();
          this.cdr.markForCheck();
        },
      });
  }

  private loadEntities(): void {
    this.httpSvc
      .requestCall(AuthEndPoints.GET_ENTITIES_LIST, ApiMethod.GET, '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const value = this.normalizeResponseValue(response);
          this.entities = this.extractEntities(value);
          this.cdr.markForCheck();
        },
        error: () => {
          this.entities = [];
          this.cdr.markForCheck();
        },
      });
  }

  private renderCharts(): void {
    if (!this.viewReady || !isPlatformBrowser(this.platformId)) {
      return;
    }

    queueMicrotask(() => {
      this.outageTypeChart?.destroy();
      this.categoryChart?.destroy();
      this.technologyChart?.destroy();
      this.mostTrippedChart?.destroy();
      this.upcomingChart?.destroy();

      this.outageTypeChart = this.createDonutChart(
        this.outageTypeChartContainer?.nativeElement,
        'Outage Type Wise',
        this.outageTypeWise,
      );
      this.categoryChart = this.createDonutChart(
        this.categoryChartContainer?.nativeElement,
        'Category Wise Outage Share',
        this.categoryWise,
      );
      this.technologyChart = this.createDonutChart(
        this.technologyChartContainer?.nativeElement,
        'Technology Wise Outage Share',
        this.technologyWise,
      );
      this.mostTrippedChart = this.createComboChart(
        this.mostTrippedChartContainer?.nativeElement,
        this.getMostTrippedTitle(),
        this.mostTrippedPoints.map((item) => item.name),
        this.mostTrippedPoints.map((item) => item.count),
        this.mostTrippedPoints.map((item) => item.capacity),
        'Trip Count',
        'Capacity (MW)',
      );
      this.upcomingChart = this.createComboChart(
        this.upcomingChartContainer?.nativeElement,
        this.getUpcomingTitle(),
        this.upcomingPoints.map((item) => item.label),
        this.upcomingPoints.map((item) => item.count),
        this.upcomingPoints.map((item) => item.capacity),
        'Outage Count',
        'Capacity (MW)',
      );
    });
  }

  private createDonutChart(
    container: HTMLDivElement | undefined,
    title: string,
    data: OutageAnalyticsChartPoint[],
  ): Highcharts.Chart | undefined {
    if (!container) {
      return undefined;
    }

    return Highcharts.chart(container, {
      chart: {
        type: 'pie',
        backgroundColor: 'transparent',
        style: {
          fontFamily: 'var(--app-font-sans)',
        },
      },
      title: { text: undefined },
      credits: { enabled: false },
      tooltip: {
        backgroundColor: '#0f172a',
        borderRadius: 12,
        style: { color: '#fff' },
        pointFormat: '<b>{point.y:.2f}%</b><br/>Capacity: <b>{point.capacity:.2f}</b>',
      },
      plotOptions: {
        pie: {
          innerSize: '62%',
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            distance: 14,
            connectorColor: '#94a3b8',
            format: '{point.name}',
            style: {
              color: '#1e293b',
              textOutline: 'none',
              fontSize: '0.82rem',
            },
          },
        },
      },
      colors: ['#2563eb', '#7c3aed', '#06b6d4', '#f97316', '#10b981', '#ef4444', '#f59e0b'],
      series: [
        {
          type: 'pie',
          name: title,
          data: data.map((item) => ({
            name: item.name,
            y: item.y,
            capacity: item.capacity,
          })),
        },
      ],
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 480,
            },
            chartOptions: {
              plotOptions: {
                pie: {
                  dataLabels: {
                    distance: 8,
                  },
                },
              },
            },
          },
        ],
      },
    } as Highcharts.Options);
  }

  private createComboChart(
    container: HTMLDivElement | undefined,
    title: string,
    categories: string[],
    countData: number[],
    capacityData: number[],
    countName: string,
    capacityName: string,
  ): Highcharts.Chart | undefined {
    if (!container) {
      return undefined;
    }

    return Highcharts.chart(container, {
      chart: {
        type: 'column',
        backgroundColor: 'transparent',
        style: {
          fontFamily: 'var(--app-font-sans)',
        },
      },
      title: { text: undefined },
      credits: { enabled: false },
      xAxis: {
        categories,
        labels: {
          style: {
            color: '#94a3b8',
            fontSize: '0.8rem',
          },
        },
      },
      yAxis: [
        {
          title: {
            text: countName,
            style: {
              color: '#94a3b8',
            },
          },
          labels: {
            style: {
              color: '#94a3b8',
            },
          },
        },
        {
          title: {
            text: capacityName,
            style: {
              color: '#94a3b8',
            },
          },
          labels: {
            style: {
              color: '#94a3b8',
            },
          },
          opposite: true,
        },
      ],
      legend: {
        align: 'center',
        verticalAlign: 'bottom',
      },
      tooltip: {
        shared: true,
        backgroundColor: '#0f172a',
        borderRadius: 12,
        style: { color: '#fff' },
      },
      plotOptions: {
        column: {
          borderRadius: 6,
          borderWidth: 0,
        },
        series: {
          marker: {
            enabled: true,
          },
        },
      },
      series: [
        {
          type: 'column',
          name: countName,
          data: countData,
          color: '#0ea5e9',
          yAxis: 0,
        },
        {
          type: 'line',
          name: capacityName,
          data: capacityData,
          color: '#ef4444',
          yAxis: 1,
        },
      ],
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 480,
            },
            chartOptions: {
              legend: {
                layout: 'horizontal',
              },
            },
          },
        ],
      },
    } as Highcharts.Options);
  }

  private extractTodayRows(response: unknown): OutageAnalyticsRow[] {
    const value = this.normalizeResponseValue(response);
    const rows = this.getRecordValue(value, 'today_outages');
    if (!Array.isArray(rows)) {
      return [];
    }

    return rows.map((item) => this.mapOutageRow(item));
  }

  private extractSummary(response: unknown): OutageAnalyticsSummary | null {
    const value = this.normalizeResponseValue(response);
    const summary = this.getRecordValue(value, 'summary');
    if (!summary || typeof summary !== 'object') {
      return null;
    }

    const record = this.getRecord(summary);
    return {
      selected_date: typeof record['selected_date'] === 'string' ? record['selected_date'] : '',
      total_records: Number(record['total_records'] ?? 0),
      total_capacity: Number(record['total_capacity'] ?? 0),
      dominant_outage_type: typeof record['dominant_outage_type'] === 'string' ? record['dominant_outage_type'] : '-',
      dominant_outage_count: Number(record['dominant_outage_count'] ?? 0),
    };
  }

  private extractEntities(response: unknown): OutageAnalyticsEntityOption[] {
    const value = this.normalizeResponseValue(response);
    const rows = Array.isArray(value) ? value : this.getRecordValue(value, 'entities');

    if (!Array.isArray(rows)) {
      return [];
    }

    return rows.map((item) => {
      const record = this.getRecord(item);
      return {
        id: Number(record['id'] ?? 0),
        entity_name: typeof record['entity_name'] === 'string' ? record['entity_name'] : '-',
      };
    });
  }

  private extractComboPoints(response: unknown): OutageAnalyticsComboPoint[] {
    const value = this.normalizeResponseValue(response);
    const rows = this.getRecordValue(value, 'rows');
    if (!Array.isArray(rows)) {
      return [];
    }

    return rows.map((item) => {
      const record = this.getRecord(item);
      return {
        name: typeof record['station_name'] === 'string' ? record['station_name'] : '-',
        count: Number(record['tripped_count'] ?? 0),
        capacity: Number(record['capacity'] ?? 0),
      };
    });
  }

  private extractTimelinePoints(response: unknown): OutageAnalyticsTimelinePoint[] {
    const value = this.normalizeResponseValue(response);
    const rows = this.getRecordValue(value, 'rows');
    if (!Array.isArray(rows)) {
      return [];
    }

    return rows.map((item) => {
      const record = this.getRecord(item);
      return {
        date: typeof record['date'] === 'string' ? record['date'] : '',
        label: typeof record['label'] === 'string' ? record['label'] : '',
        count: Number(record['count'] ?? 0),
        capacity: Number(record['capacity'] ?? 0),
      };
    });
  }

  private extractPagination(response: unknown): { page_no: number; page_size: number; total_records: number; total_pages: number } | null {
    const value = this.normalizeResponseValue(response);
    const pagination = this.getRecordValue(value, 'pagination');
    if (!pagination || typeof pagination !== 'object') {
      return null;
    }

    const record = this.getRecord(pagination);
    return {
      page_no: Number(record['page_no'] ?? 1),
      page_size: Number(record['page_size'] ?? this.pageSizeControl.value),
      total_records: Number(record['total_records'] ?? 0),
      total_pages: Number(record['total_pages'] ?? 0),
    };
  }

  private getDefaultChartStartDate(): Date {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  }

  private extractCharts(response: unknown): { outage_type_wise: OutageAnalyticsChartPoint[]; category_wise: OutageAnalyticsChartPoint[]; technology_wise: OutageAnalyticsChartPoint[] } {
    const value = this.normalizeResponseValue(response);
    const charts = this.getRecordValue(value, 'charts');
    const record = this.getRecord(charts);

    return {
      outage_type_wise: this.mapChartItems(record['outage_type_wise']),
      category_wise: this.mapChartItems(record['category_wise']),
      technology_wise: this.mapChartItems(record['technology_wise']),
    };
  }

  private mapChartItems(value: unknown): OutageAnalyticsChartPoint[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((item) => {
      const record = this.getRecord(item);
      return {
        name: typeof record['name'] === 'string' ? record['name'] : '-',
        y: Number(record['y'] ?? 0),
        capacity: Number(record['capacity'] ?? 0),
        count: Number(record['count'] ?? 0),
      };
    });
  }

  private mapOutageRow(item: unknown): OutageAnalyticsRow {
    const record = this.getRecord(item);
    return {
      id: Number(record['id'] ?? 0),
      serial_no: Number(record['serial_no'] ?? record['s_no'] ?? 0),
      outage_type: typeof record['outage_type'] === 'string' ? record['outage_type'] : '-',
      generator_type: typeof record['generator_type'] === 'string' ? record['generator_type'] : '-',
      name: typeof record['name'] === 'string' ? record['name'] : '-',
      capacity: Number(record['capacity'] ?? 0),
      share: Number(record['share'] ?? 0),
      reason: typeof record['reason'] === 'string' ? record['reason'] : '-',
    };
  }

  private normalizeResponseValue(response: unknown): Record<string, unknown> {
    if (!response || typeof response !== 'object') {
      return {};
    }

    const record = this.getRecord(response);
    if (this.hasAnalyticsKeys(record)) {
      return record;
    }

    const rootValue = record['value'];
    if (rootValue && typeof rootValue === 'object') {
      return this.getRecord(rootValue);
    }

    return record;
  }

  private hasAnalyticsKeys(record: Record<string, unknown>): boolean {
    return Boolean(
      record['today_outages'] !== undefined ||
      record['charts'] !== undefined ||
      record['summary'] !== undefined ||
      record['rows'] !== undefined ||
      record['entities'] !== undefined ||
      record['series'] !== undefined ||
      record['categories'] !== undefined,
    );
  }

  private buildQueryRoute(baseRoute: string, params: Record<string, unknown>): string {
    const query = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      if (value === null || value === undefined || value === '') {
        continue;
      }

      if (typeof value === 'string') {
        query.set(key, value);
        continue;
      }

      if (typeof value === 'number' || typeof value === 'boolean') {
        query.set(key, value.toString());
      }
    }

    const queryString = query.toString();
    return queryString ? `${baseRoute}?${queryString}` : baseRoute;
  }

  private resolveDateForMode(mode: OutageAnalyticsDateMode): Date {
    const date = new Date();
    if (mode === 'yesterday') {
      date.setDate(date.getDate() - 1);
    }

    return date;
  }

  private formatQueryDate(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getRecordValue(value: unknown, key: string): unknown {
    const record = this.getRecord(value);
    return record[key] ?? null;
  }

  private getRecord(value: unknown): Record<string, unknown> {
    if (!value || typeof value !== 'object') {
      return {};
    }

    return Object.fromEntries(Object.entries(value));
  }
}
